import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import { Modal } from "../../components/Shell";

const QuestionForm = ({ certId, initial, onSaved, onClose }) => {
  const [prompt, setPrompt] = useState(initial?.prompt || "");
  const [options, setOptions] = useState(initial?.options || ["", "", "", ""]);
  const [answer, setAnswer] = useState(initial?.answer ?? 0);
  const [error, setError] = useState("");
  const setOption = (i, v) => setOptions(options.map((o, j) => (j === i ? v : o)));
  const removeOption = (i) => { setOptions(options.filter((_, j) => j !== i)); if (answer >= i && answer > 0) setAnswer(answer - 1); };
  const save = async (e) => {
    e.preventDefault(); setError("");
    const body = { prompt, options, answer };
    try { initial ? await api.put(`/admin/questions/${initial.id}`, body) : await api.post(`/admin/certifications/${certId}/questions`, body); toast.success("Question saved"); onSaved(); }
    catch (err) { setError(formatError(err)); }
  };
  return (
    <Modal open onClose={onClose} title={initial ? "Edit question" : "New question"} testId="question-form-dialog">
      <form className="admin-form" onSubmit={save}>
        <label className="form-field"><span>Question</span><textarea data-testid="question-form-prompt" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} required /></label>
        <span className="form-label">Options — select the correct answer</span>
        {options.map((o, i) => (
          <div className="option-edit" key={i}>
            <input type="radio" name="answer" data-testid={`question-form-correct-${i}`} checked={answer === i} onChange={() => setAnswer(i)} aria-label={`Mark option ${i + 1} correct`} />
            <input data-testid={`question-form-option-${i}`} value={o} onChange={(e) => setOption(i, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + i)}`} required />
            {options.length > 2 && <button type="button" className="icon-button" data-testid={`question-form-remove-option-${i}`} onClick={() => removeOption(i)} aria-label="Remove option"><X size={14} /></button>}
          </div>))}
        {options.length < 6 && <button type="button" className="text-link-button" data-testid="question-form-add-option" onClick={() => setOptions([...options, ""])}><Plus size={14} /> Add option</button>}
        {error && <div className="error" data-testid="question-form-error">{error}</div>}
        <button className="button primary full" data-testid="question-form-submit">Save question</button>
      </form>
    </Modal>
  );
};

export const QuestionsAdmin = ({ certs, refresh }) => {
  const [certId, setCertId] = useState("");
  const [questions, setQuestions] = useState([]);
  const [editing, setEditing] = useState(null);
  const active = certId || certs[0]?.id || "";
  const load = useCallback(() => { if (active) api.get(`/admin/certifications/${active}/questions`).then((r) => setQuestions(r.data)).catch((e) => toast.error(formatError(e))); }, [active]);
  useEffect(() => { load(); }, [load]);
  const after = () => { setEditing(null); load(); refresh(); };
  const remove = async (q) => {
    if (!window.confirm("Delete this question?")) return;
    try { await api.delete(`/admin/questions/${q.id}`); toast.success("Question deleted"); after(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div>
      <div className="section-title"><h2>Question bank</h2>
        <div className="toolbar"><select data-testid="questions-cert-select" value={active} onChange={(e) => setCertId(e.target.value)}>{certs.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</select>
          <button className="button primary" data-testid="new-question-button" disabled={!active} onClick={() => setEditing("new")}><Plus size={16} /> New question</button></div></div>
      {!questions.length && <div className="empty-state" data-testid="questions-empty-state">No questions yet for this certification.</div>}
      <div className="question-admin-list">{questions.map((q, i) => (
        <div className="question-admin" key={q.id} data-testid={`question-admin-${i + 1}`}>
          <div className="question-number">{String(i + 1).padStart(2, "0")}</div>
          <div className="qa-body"><b>{q.prompt}</b><ol>{q.options.map((o, j) => <li key={j} className={j === q.answer ? "correct" : ""}>{o}</li>)}</ol></div>
          <div className="row-actions"><button className="icon-button" data-testid={`edit-question-${i + 1}`} onClick={() => setEditing(q)} aria-label="Edit"><Pencil size={16} /></button><button className="icon-button danger" data-testid={`delete-question-${i + 1}`} onClick={() => remove(q)} aria-label="Delete"><Trash2 size={16} /></button></div>
        </div>))}</div>
      {editing && <QuestionForm certId={active} initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={after} />}
    </div>
  );
};
